import { zodResolver } from "@hookform/resolvers/zod";
import { formatISO } from "date-fns";
import { ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ControllerRenderProps, FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useMutation } from "urql";
import { z } from "zod";

import { Protect, usePermissionCheck } from "@/components/Protect";
import { SearchCommentatorInput } from "@/components/SearchInput";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { TournamentIdInput } from "@/components/inputs/TournamentIdInput";
import { TournamentSeriesIdInput } from "@/components/inputs/TournamentSeriesIdInput";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Form,
    FormCardSection,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { PlayerForm_PlayerFragment, UpdatePlayerInput } from "@/gen/graphql";
import { Permissions } from "@/hooks/usePermissions";
import { CommentatorT } from "@/hooks/useSearchCommentators";
import { cn } from "@/utils";
import { COUNTRY_CODES, CountryCode, countryNamesByCodes, getFlagEmoji } from "@/utils/i18n";
import { generateImageUrl } from "@/utils/image";

const PlayerForm_Player = graphql(`
    fragment PlayerForm_Player on Player {
        id
        nickname
        imageId
        happyImageId
        neutralImageId
        sadImageId
        dateOfBirth
        editorialPriority
        lichessUsername
        chesscomUsername
        nameTag
        playingStyle {
            id: playerId
            curatedBy
            curatedByCommentator {
                id
                displayName
            }
            endgame
            middlegame
            opening
            tactics
            strategy
            blitz
            whitePieces
            blackPieces
            attack
            calculation
            positional
            creative
            preparation
            discipline
        }

        skills {
            id: playerId
            overall
            attack
            defense
            calculation
            strength
            intuition
            timeManagement
        }

        playerCards {
            id
            imageId
            tournamentId
            tournament {
                id
                title
            }
            tournamentSeriesId
            tournamentSeries {
                id
                title
            }
        }
    }
`);

const formMutation = graphql(`
    mutation updatePlayer($input: UpdatePlayerInput!) {
        updatePlayer(input: $input) {
            id
            name
            nickname
            imageId
            happyImageId
            neutralImageId
            sadImageId
            dateOfBirth
            editorialPriority
            nameTag
        }
    }
`);

const createPlayerCardMutation = graphql(`
    mutation createPlayerCard($input: CreatePlayerCardInput!) {
        createPlayerCard(input: $input) {
            id
        }
    }
`);

const updatePlayerCardMutation = graphql(`
    mutation updatePlayerCard($input: UpdatePlayerCardInput!) {
        updatePlayerCard(input: $input) {
            id
        }
    }
`);

const deletePlayerCardMutation = graphql(`
    mutation deletePlayerCard($id: UUID!) {
        deletePlayerCard(id: $id)
    }
`);

const zBoundedPercent = z.coerce
    .number()
    .min(0)
    .max(1)
    .default(0.5)
    .transform(v => String(v));

const zPlayingStyle = z.object({
    endgame: zBoundedPercent,
    middlegame: zBoundedPercent,
    opening: zBoundedPercent,
    tactics: zBoundedPercent,
    strategy: zBoundedPercent,
    blitz: zBoundedPercent,
    whitePieces: zBoundedPercent,
    blackPieces: zBoundedPercent,
    attack: zBoundedPercent,
    calculation: zBoundedPercent,
    positional: zBoundedPercent,
    creative: zBoundedPercent,
    preparation: zBoundedPercent,
    discipline: zBoundedPercent,
    curatedBy: z.string().nullable().default(null),
});

const zSkillNumber = z.number().int().min(1).max(100).default(1);

const zSkills = z.object({
    overall: zSkillNumber,
    attack: zSkillNumber,
    defense: zSkillNumber,
    calculation: zSkillNumber,
    strength: zSkillNumber,
    intuition: zSkillNumber,
    timeManagement: zSkillNumber,
});

const zUpdatePlayerInput = z.object({
    id: z.string(),
    name: z.string().min(1),
    nickname: z.string(),
    fideTitle: z.enum(["GM", "IM", "FM", "CM", "WCM", "WFM", "WGM", "WH", "WIM"]).nullable(),
    countryCode: z.enum(COUNTRY_CODES).nullable(),
    dateOfBirth: z.string().date().nullable(),
    imageId: z.string().nullable(),
    happyImageId: z.string().nullable(),
    neutralImageId: z.string().nullable(),
    sadImageId: z.string().nullable(),
    playingStyle: zPlayingStyle.nullable().default(null),
    skills: zSkills.nullable().default(null),
    editorialPriority: z.number().nullable(),
    lichessUsername: z.string().nullable().default(null),
    chesscomUsername: z.string().nullable().default(null),
    nameTag: z
        .preprocess(
            v => (v === "string" && v.trim() === "" ? null : v),
            z
                .string()
                .min(2, { message: "Name tag must be 2 – 3 characters long" })
                .max(3, { message: "Name tag must be 2 – 3 characters long" })
                .nullable() // after preprocess the value might be null
        )
        .default(null),
});

export const PlayerForm = ({
    playerFragment,
}: {
    playerFragment: FragmentType<typeof PlayerForm_Player>;
}) => {
    const player = getFragmentData(PlayerForm_Player, playerFragment);
    const canEditPlayer = usePermissionCheck(Permissions.PLAYER_MANAGEMENT);

    const defaults = useMemo(() => zUpdatePlayerInput.parse(player), [player]);
    const form = useForm<z.infer<typeof zUpdatePlayerInput>>({
        defaultValues: defaults,
        mode: "onBlur",
        resolver: zodResolver(zUpdatePlayerInput),
    });

    const initialCards = useMemo(() => player.playerCards ?? [], [player]);
    const { reset } = form;
    const lastIdRef = useRef(player.id);

    useEffect(() => {
        if (player.id !== lastIdRef.current) {
            lastIdRef.current = player.id;
            reset(defaults);
            setPlayerCards(initialCards);
        }
    }, [player.id, defaults, initialCards, reset]);

    const playingStyle = form.watch("playingStyle");
    const initialPlayingStyle = playingStyle ?? defaults.playingStyle ?? zPlayingStyle.parse({});

    const renderPlayingStyleToggle = useCallback(
        (open: boolean, handleOpenChanged: (v: boolean) => void) => (
            <Switch checked={open} onCheckedChange={checked => handleOpenChanged(checked)} />
        ),
        []
    );

    const skills = form.watch("skills");
    const initialSkills = skills ?? defaults.skills ?? zSkills.parse({});

    const renderSkillsToggle = useCallback(
        (open: boolean, handleOpenChanged: (v: boolean) => void) => (
            <Switch checked={open} onCheckedChange={checked => handleOpenChanged(checked)} />
        ),
        []
    );

    const [{ error: _errorAddRecord }, updatePlayer] = useMutation(formMutation);
    const [, createPlayerCard] = useMutation(createPlayerCardMutation);
    const [, updatePlayerCard] = useMutation(updatePlayerCardMutation);
    const [, deletePlayerCard] = useMutation(deletePlayerCardMutation);

    // State for managing player cards
    const [playerCards, setPlayerCards] = useState<
        (
            | PlayerForm_PlayerFragment["playerCards"][number]
            | (Omit<PlayerForm_PlayerFragment["playerCards"][number], "id"> & { id?: string })
        )[]
    >(player.playerCards || []);

    const handleAddPlayerCard = () => {
        setPlayerCards([
            ...playerCards,
            {
                imageId: "",
                tournamentId: null,
                tournamentSeriesId: null,
                tournament: null,
                tournamentSeries: null,
            },
        ]);
    };

    const handleRemovePlayerCard = (index: number) => {
        setPlayerCards(playerCards.filter((_, i) => i !== index));
    };

    const handleUpdatePlayerCardData = (index: number, field: string, value: string | null) => {
        setPlayerCards(
            playerCards.map((card, i) => {
                if (i !== index) return card;

                // Handle tournament selection
                if (field === "tournamentId") {
                    return {
                        ...card,
                        [field]: value,
                        tournament: value ? { id: value, title: "Loading..." } : null,
                        // Clear tournament series when tournament is selected
                        tournamentSeriesId: value ? null : card.tournamentSeriesId,
                        tournamentSeries: value ? null : card.tournamentSeries,
                    };
                }

                // Handle tournament series selection
                if (field === "tournamentSeriesId") {
                    return {
                        ...card,
                        [field]: value,
                        tournamentSeries: value ? { id: value, title: "Loading..." } : null,
                        // Clear tournament when tournament series is selected
                        tournamentId: value ? null : card.tournamentId,
                        tournament: value ? null : card.tournament,
                    };
                }

                return { ...card, [field]: value };
            })
        );
    };

    const onSubmit: SubmitHandler<UpdatePlayerInput> = async input => {
        const result = await updatePlayer({
            input,
        });
        if (result.error) {
            toast.error("Unable to update player", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Player was updated");
        }
    };

    const handleCreatePlayerCard = async (cardData: {
        imageId: string;
        tournamentId: string | null;
        tournamentSeriesId: string | null;
    }) => {
        const result = await createPlayerCard({
            input: {
                playerId: player.id,
                imageId: cardData.imageId,
                tournamentId: cardData.tournamentId,
                tournamentSeriesId: cardData.tournamentSeriesId,
            },
        });

        if (result.error) {
            toast.error("Unable to create player card", { description: result.error.message });
        } else {
            toast.success("Player card created successfully");
            // Refresh or update local state as needed
        }
    };

    const handleUpdatePlayerCard = async (
        cardId: string,
        cardData: {
            imageId: string;
            tournamentId: string | null;
            tournamentSeriesId: string | null;
        }
    ) => {
        const result = await updatePlayerCard({
            input: {
                id: cardId,
                imageId: cardData.imageId,
                tournamentId: cardData.tournamentId,
                tournamentSeriesId: cardData.tournamentSeriesId,
            },
        });

        if (result.error) {
            toast.error("Unable to update player card", { description: result.error.message });
        } else {
            toast.success("Player card updated successfully");
        }
    };

    const handleDeletePlayerCard = async (cardId: string, cardIndex: number) => {
        if (
            window.confirm(
                "Are you sure you want to delete this player card? This action cannot be undone."
            )
        ) {
            const result = await deletePlayerCard({ id: cardId });

            if (result.error) {
                toast.error("Unable to delete player card", { description: result.error.message });
            } else {
                toast.success("Player card deleted successfully");
                handleRemovePlayerCard(cardIndex);
            }
        }
    };

    const [isCountryCodePopoverOpen, setCountryCodePopoverOpen] = useState(false);

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormCardSection
                    title="Basic information"
                    description="Shown just about everywhere."
                    defaultOpen
                >
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Full name</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="Magnus Carlsen"
                                        {...field}
                                        disabled={!canEditPlayer}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="nickname"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Nickname</FormLabel>
                                <FormControl>
                                    <Input placeholder="E.g. Magnus" {...field} />
                                </FormControl>
                                <FormDescription>Used in informal settings.</FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="nameTag"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Name tag</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="E.g. Mag"
                                        {...field}
                                        value={field.value ?? ""}
                                    />
                                </FormControl>
                                <FormDescription>Used in knockout brackets.</FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="lichessUsername"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Lichess username</FormLabel>
                                <FormControl>
                                    <Input {...field} value={field.value ?? ""} />
                                </FormControl>
                                <FormDescription>
                                    Username used to connect lichess user to player
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="chesscomUsername"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Chess.com username</FormLabel>
                                <FormControl>
                                    <Input {...field} value={field.value ?? ""} />
                                </FormControl>
                                <FormDescription>
                                    Username used to connect chess.com user to player
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="editorialPriority"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Editorial Priority</FormLabel>
                                <FormControl>
                                    <Input
                                        type="number"
                                        placeholder=""
                                        {...field}
                                        value={field.value ?? ""}
                                        onChange={e => {
                                            field.onChange(e.target.valueAsNumber);
                                        }}
                                    />
                                </FormControl>
                                <FormDescription>
                                    Leave empty to not show the player. Higher is lower priority
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="fideTitle"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>FIDE Title</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val =>
                                            field.onChange(val === "-" ? null : val)
                                        }
                                        value={field.value ?? "-"}
                                        name={field.name}
                                    >
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder="No title" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="-">No title</SelectItem>
                                            <SelectItem value="GM">GM</SelectItem>
                                            <SelectItem value="IM">IM</SelectItem>
                                            <SelectItem value="FM">FM</SelectItem>
                                            <SelectItem value="CM">CM</SelectItem>
                                            <SelectItem value="WCM">WCM</SelectItem>
                                            <SelectItem value="WFM">WFM</SelectItem>
                                            <SelectItem value="WGM">WGM</SelectItem>
                                            <SelectItem value="WH">WH</SelectItem>
                                            <SelectItem value="WIM">WIM</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="countryCode"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Country</FormLabel>
                                <div>
                                    <Popover
                                        open={isCountryCodePopoverOpen}
                                        onOpenChange={setCountryCodePopoverOpen}
                                    >
                                        <PopoverTrigger asChild>
                                            <FormControl>
                                                <Button
                                                    variant="outline"
                                                    role="combobox"
                                                    className={cn(
                                                        "w-full justify-between",
                                                        !field.value && "text-muted-foreground"
                                                    )}
                                                >
                                                    {field.value
                                                        ? `${getFlagEmoji(field.value as CountryCode)} ${
                                                              countryNamesByCodes[
                                                                  field.value as CountryCode
                                                              ]
                                                          }`
                                                        : "Select countries..."}
                                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[400px] p-0" align="start">
                                            <Command>
                                                <CommandInput placeholder="Search countries..." />
                                                <CommandList>
                                                    <CommandEmpty>No countries found.</CommandEmpty>
                                                    <CommandGroup>
                                                        {Object.entries(countryNamesByCodes).map(
                                                            ([countryCode, name]) => (
                                                                <CommandItem
                                                                    value={name}
                                                                    key={countryCode}
                                                                    onSelect={() => {
                                                                        form.setValue(
                                                                            "countryCode",
                                                                            countryCode as CountryCode
                                                                        );
                                                                        setCountryCodePopoverOpen(
                                                                            false
                                                                        );
                                                                    }}
                                                                >
                                                                    {getFlagEmoji(
                                                                        countryCode as CountryCode
                                                                    )}{" "}
                                                                    {name}
                                                                </CommandItem>
                                                            )
                                                        )}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="dateOfBirth"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Date of birth</FormLabel>
                                <FormControl>
                                    <Input
                                        type="date"
                                        placeholder="YYYY-MM-DD"
                                        {...field}
                                        value={field.value ?? ""}
                                        min="1800-01-01"
                                        max={formatISO(new Date(), { representation: "date" })}
                                    />
                                </FormControl>
                                <FormDescription>Not available from FIDE.</FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormCardSection>
                <FormCardSection title="Player images" description="In all of their glory.">
                    <FormField
                        control={form.control}
                        name="imageId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Main image</FormLabel>
                                <FormControl>
                                    <ImageUploadInput
                                        imageId={field.value ?? null}
                                        onImageId={imageId => form.setValue(field.name, imageId)}
                                    />
                                </FormControl>
                                <FormDescription>
                                    This variant is used in informal settings.
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="happyImageId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Happy image</FormLabel>
                                <FormControl>
                                    <ImageUploadInput
                                        imageId={field.value ?? null}
                                        onImageId={imageId => form.setValue(field.name, imageId)}
                                    />
                                </FormControl>
                                <FormDescription>
                                    This variant may be used when the player is doing well.
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="neutralImageId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Pondering image</FormLabel>
                                <FormControl>
                                    <ImageUploadInput
                                        imageId={field.value ?? null}
                                        onImageId={imageId => form.setValue(field.name, imageId)}
                                    />
                                </FormControl>
                                <FormDescription>
                                    This variant may be used when the player is pondering.
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="sadImageId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Sad image</FormLabel>
                                <FormControl>
                                    <ImageUploadInput
                                        imageId={field.value ?? null}
                                        onImageId={imageId => form.setValue(field.name, imageId)}
                                    />
                                </FormControl>
                                <FormDescription>
                                    This variant may be used when the player is struggling or
                                    losing.
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormCardSection>
                <FormCardSection
                    title="Playing style"
                    description="As judged by humans such as yourself."
                    open={playingStyle !== null}
                    onOpenChange={open => {
                        form.setValue("playingStyle", open ? initialPlayingStyle : null);
                    }}
                    renderToggleControl={renderPlayingStyleToggle}
                >
                    <h3>Strengths</h3>
                    <FormField
                        control={form.control}
                        name="playingStyle.endgame"
                        render={({ field }) => <SliderFormItem label="Endgame" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.middlegame"
                        render={({ field }) => <SliderFormItem label="Middlegame" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.opening"
                        render={({ field }) => <SliderFormItem label="Opening" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.tactics"
                        render={({ field }) => <SliderFormItem label="Tactics" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.strategy"
                        render={({ field }) => <SliderFormItem label="Strategy" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.blitz"
                        render={({ field }) => <SliderFormItem label="Blitz" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.whitePieces"
                        render={({ field }) => (
                            <SliderFormItem label="White pieces" field={field} />
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.blackPieces"
                        render={({ field }) => (
                            <SliderFormItem label="Black pieces" field={field} />
                        )}
                    />
                    <h3>Tradeoffs</h3>
                    <FormField
                        control={form.control}
                        name="playingStyle.attack"
                        render={({ field }) => (
                            <SliderFormItem label="Attack" rightLabel="Defense" field={field} />
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.calculation"
                        render={({ field }) => (
                            <SliderFormItem
                                label="Calculation"
                                rightLabel="Intuition"
                                field={field}
                            />
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.positional"
                        render={({ field }) => (
                            <SliderFormItem
                                label="Positional"
                                rightLabel="Tactical"
                                field={field}
                            />
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.creative"
                        render={({ field }) => (
                            <SliderFormItem label="Creative" rightLabel="Solid" field={field} />
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.preparation"
                        render={({ field }) => (
                            <SliderFormItem
                                label="Preparation"
                                rightLabel="Improvisation"
                                field={field}
                            />
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="playingStyle.discipline"
                        render={({ field }) => (
                            <SliderFormItem label="Discipline" rightLabel="Rebel" field={field} />
                        )}
                    />
                    <h3>Commentator</h3>
                    <FormField
                        control={form.control}
                        name="playingStyle.curatedBy"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Curated by</FormLabel>
                                <FormControl>
                                    <CommentatorIdInput
                                        initialValue={
                                            player?.playingStyle?.curatedByCommentator ?? null
                                        }
                                        onChange={field.onChange}
                                    />
                                </FormControl>
                                <FormDescription>
                                    Who's name should be noted as "Curated by ..."?
                                </FormDescription>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormCardSection>{" "}
                <FormCardSection
                    title="Skills"
                    description="Data used for player cards."
                    open={skills !== null}
                    onOpenChange={open => {
                        form.setValue("skills", open ? initialSkills : null);
                    }}
                    renderToggleControl={renderSkillsToggle}
                >
                    <h3>Skills</h3>
                    <FormField
                        control={form.control}
                        name="skills.overall"
                        render={({ field }) => <SkillFormItem label="Overall" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="skills.attack"
                        render={({ field }) => <SkillFormItem label="Attack" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="skills.defense"
                        render={({ field }) => <SkillFormItem label="Defense" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="skills.calculation"
                        render={({ field }) => <SkillFormItem label="Calculation" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="skills.strength"
                        render={({ field }) => <SkillFormItem label="Strength" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="skills.intuition"
                        render={({ field }) => <SkillFormItem label="Intuition" field={field} />}
                    />
                    <FormField
                        control={form.control}
                        name="skills.timeManagement"
                        render={({ field }) => (
                            <SkillFormItem label="Time management" field={field} />
                        )}
                    />
                </FormCardSection>
                <FormCardSection
                    title="Player Cards"
                    description="Tournament and series specific player card images."
                >
                    <div className="space-y-4">
                        {playerCards.map((card, index) => {
                            const cardTitle =
                                card.tournament?.title ||
                                card.tournamentSeries?.title ||
                                `Player Card ${index + 1}`;
                            return (
                                <div
                                    key={card.id || `new-${index}`}
                                    className="space-y-4 rounded-lg border p-4"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex flex-1 items-start gap-4">
                                            {card.imageId && (
                                                <div className="h-24 w-20 flex-shrink-0 overflow-hidden rounded-md bg-gray-100">
                                                    <img
                                                        src={generateImageUrl({
                                                            imageId: card.imageId,
                                                            width: 80,
                                                            height: 96,
                                                        })}
                                                        alt={`Player card for ${cardTitle}`}
                                                        className="h-full w-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1">
                                                <h4 className="font-medium">{cardTitle}</h4>
                                                {card.tournament && (
                                                    <p className="text-sm text-gray-600">
                                                        Tournament: {card.tournament.title}
                                                    </p>
                                                )}
                                                {card.tournamentSeries && (
                                                    <p className="text-sm text-gray-600">
                                                        Tournament Series:{" "}
                                                        {card.tournamentSeries.title}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                if (card.id) {
                                                    handleDeletePlayerCard(card.id, index);
                                                } else {
                                                    handleRemovePlayerCard(index);
                                                }
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>

                                    <div>
                                        <FormLabel>Card Image</FormLabel>
                                        <ImageUploadInput
                                            imageId={card.imageId || null}
                                            onImageId={imageId =>
                                                handleUpdatePlayerCardData(
                                                    index,
                                                    "imageId",
                                                    imageId
                                                )
                                            }
                                        />
                                        {!card.imageId && (
                                            <p className="mt-1 text-sm text-red-600">
                                                Image is required
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <FormLabel>Tournament</FormLabel>
                                        <TournamentIdInput
                                            tournamentId={card.tournamentId}
                                            onTournamentIdChange={tournamentId =>
                                                handleUpdatePlayerCardData(
                                                    index,
                                                    "tournamentId",
                                                    tournamentId
                                                )
                                            }
                                        />
                                    </div>

                                    <div>
                                        <FormLabel>Tournament Series</FormLabel>
                                        <TournamentSeriesIdInput
                                            tournamentSeriesId={card.tournamentSeriesId}
                                            onTournamentSeriesIdChange={tournamentSeriesId =>
                                                handleUpdatePlayerCardData(
                                                    index,
                                                    "tournamentSeriesId",
                                                    tournamentSeriesId
                                                )
                                            }
                                        />
                                    </div>

                                    {card.id && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            disabled={!card.imageId}
                                            onClick={async () => {
                                                if (card.imageId) {
                                                    await handleUpdatePlayerCard(card.id!, {
                                                        imageId: card.imageId,
                                                        tournamentId: card.tournamentId,
                                                        tournamentSeriesId: card.tournamentSeriesId,
                                                    });
                                                } else {
                                                    toast.error(
                                                        "Please fill in all required fields correctly"
                                                    );
                                                }
                                            }}
                                        >
                                            Update Card
                                        </Button>
                                    )}

                                    {!card.id && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            disabled={!card.imageId}
                                            onClick={async () => {
                                                if (card.imageId) {
                                                    await handleCreatePlayerCard({
                                                        imageId: card.imageId,
                                                        tournamentId: card.tournamentId,
                                                        tournamentSeriesId: card.tournamentSeriesId,
                                                    });
                                                } else {
                                                    toast.error(
                                                        "Please fill in all required fields correctly"
                                                    );
                                                }
                                            }}
                                        >
                                            Create Card
                                        </Button>
                                    )}
                                </div>
                            );
                        })}

                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleAddPlayerCard}
                            className="w-full"
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Player Card
                        </Button>
                    </div>
                </FormCardSection>
                <Protect
                    permission={Permissions.PLAYER_MANAGEMENT}
                    fallback={
                        <div className="text-muted-foreground text-sm">
                            You don't have permission to edit player data
                        </div>
                    }
                >
                    <Button
                        type="submit"
                        disabled={!form.formState.isValid || form.formState.isSubmitting}
                    >
                        Save
                    </Button>
                </Protect>
            </form>
        </Form>
    );
};

function CommentatorIdInput({
    initialValue,
    onChange,
}: {
    initialValue: CommentatorT | null;
    onChange: (value: string | null) => void;
}) {
    const [commentator, setCommentator] = useState<CommentatorT | null>(initialValue ?? null);
    return (
        <SearchCommentatorInput
            commentator={commentator}
            onCommentatorChange={c => {
                setCommentator(c ?? null);
                onChange(c?.id ?? null);
            }}
        />
    );
}

function SliderFormItem<TFieldValues extends FieldValues>({
    field,
    label,
    rightLabel,
}: {
    field: ControllerRenderProps<TFieldValues>;
    label: string;
    rightLabel?: string;
}) {
    const value = parseFloat(field.value);
    return (
        <FormItem>
            <div className="flex flex-row space-x-4">
                <FormLabel className="w-1/4">{label}</FormLabel>
                <FormControl>
                    <Slider
                        {...field}
                        value={[value]}
                        onValueChange={value => field.onChange(value[0])}
                        min={0}
                        max={1}
                        step={0.01}
                    />
                </FormControl>
                <FormLabel className={cn("w-1/4", rightLabel && "text-right")}>
                    {rightLabel ?? field.value}
                </FormLabel>
            </div>
            <FormMessage />
        </FormItem>
    );
}

function SkillFormItem<TFieldValues extends FieldValues>({
    field,
    label,
}: {
    field: ControllerRenderProps<TFieldValues>;
    label: string;
}) {
    return (
        <FormItem>
            <div className="flex flex-row space-x-4">
                <FormLabel className="w-1/4">{label}</FormLabel>
                <FormControl>
                    <Input
                        type="number"
                        min={0}
                        max={100}
                        {...field}
                        value={field.value ?? ""}
                        onChange={e => {
                            field.onChange(e.target.valueAsNumber);
                        }}
                    />
                </FormControl>
            </div>
            <FormMessage />
        </FormItem>
    );
}
